import type { CellCompareDiff, InlineClassifiedContent } from "./types";

import type { ColumnType } from "@/queries/smart-tables/types";
import {
  buildPublicUrlLinkPayload,
  capClassifiedDisplayPreview,
  CellContentType,
  CLASSIFY_SCAN_LIMIT,
  classifyContent,
  isStandalonePublicUrl,
  parseMarkdownCodeFence,
} from "@/routes/smart-table/utils/contentType";

export const MEDIA_TYPES = new Set([
  CellContentType.IMAGE,
  CellContentType.AUDIO,
  CellContentType.VIDEO,
  CellContentType.PDF,
  CellContentType.FILE,
  CellContentType.MEDIA_COLLECTION,
  CellContentType.MULTI_CONTENT,
]);

export function shouldRenderAsRichText(type: CellContentType, isError: boolean): boolean {
  if (isError) {
    return false;
  }

  return type === CellContentType.PLAIN_TEXT || type === CellContentType.MARKDOWN;
}

export function isTextColumnType(columnType?: ColumnType): boolean {
  return columnType === undefined || columnType === "TEXT";
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getTypeLabel = (value: unknown): string => {
  if (value === null) {
    return "null";
  }

  if (Array.isArray(value)) {
    return "array";
  }

  return typeof value;
};

const COMPACT_DIFF_PREVIEW_LIMIT = 2_000;

const truncateWithEllipsis = (value: string, limit: number): string => {
  if (value.length <= limit) {
    return value;
  }

  return `${value.slice(0, limit)}…`;
};

const hasOversizedStringValue = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): boolean => {
  if (typeof value === "string") {
    return value.length > CLASSIFY_SCAN_LIMIT;
  }

  if (value === null || typeof value !== "object") {
    return false;
  }

  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some(item => hasOversizedStringValue(item, seen));
  }

  return Object.values(value as Record<string, unknown>).some(item =>
    hasOversizedStringValue(item, seen),
  );
};

const toDiffText = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (hasOversizedStringValue(value)) {
    return "[Large structured value omitted from inline diff]";
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

type CellCompareDiffWithPreview = CellCompareDiff & {
  beforePreview: string;
  afterPreview: string;
};

const buildCompareDiff = (
  sourceValue: unknown,
  targetValue: unknown,
  typeMismatchMessage?: string,
): CellCompareDiffWithPreview => {
  const beforeValue = toDiffText(sourceValue);
  const afterValue = toDiffText(targetValue);

  return {
    beforeValue,
    afterValue,
    beforePreview: truncateWithEllipsis(beforeValue, COMPACT_DIFF_PREVIEW_LIMIT),
    afterPreview: truncateWithEllipsis(afterValue, COMPACT_DIFF_PREVIEW_LIMIT),
    ...(typeMismatchMessage ? { typeMismatchMessage } : {}),
  };
};

const getCompareValueObject = (value: unknown): Record<string, unknown> | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (!("value" in value)) {
    return value;
  }

  const nestedValue = value.value;
  if (!isRecord(nestedValue)) {
    return value;
  }

  return nestedValue;
};

export function extractCompareDiff(value: unknown): CellCompareDiff | null {
  const compareValue = getCompareValueObject(value);
  const hasCompareValue = compareValue !== null;

  if (!hasCompareValue) {
    return null;
  }

  const hasSource = Object.prototype.hasOwnProperty.call(compareValue, "source");
  const hasTarget = Object.prototype.hasOwnProperty.call(compareValue, "target");

  if (!hasSource || !hasTarget) {
    return null;
  }

  const sourceValue = compareValue.source;
  const targetValue = compareValue.target;
  const sourceType = getTypeLabel(sourceValue);
  const targetType = getTypeLabel(targetValue);

  if (sourceType !== targetType) {
    return buildCompareDiff(
      sourceValue,
      targetValue,
      `Type mismatch: source is ${sourceType}, target is ${targetType}`,
    );
  }

  return buildCompareDiff(sourceValue, targetValue);
}

export function classifyTextValue(
  value: unknown,
  displayType?: "boolean" | "number" | "object" | "string" | "unknown" | "html" | "markdown",
): InlineClassifiedContent {
  if (value === null || value === undefined || value === "") {
    return {
      type: CellContentType.EMPTY,
      displayPreview: "",
      rawValue: value,
      parsedValue: value,
      isStructuredType: false,
    };
  }

  if (typeof value === "object") {
    const classifiedObject = classifyContent(value);
    if (MEDIA_TYPES.has(classifiedObject.type)) {
      return classifiedObject;
    }

    if (hasOversizedStringValue(value)) {
      return {
        type: CellContentType.JSON,
        displayPreview: "[Large structured value]",
        rawValue: value,
        parsedValue: value,
        isStructuredType: true,
      };
    }

    try {
      return {
        type: CellContentType.JSON,
        displayPreview: capClassifiedDisplayPreview(JSON.stringify(value)),
        rawValue: value,
        parsedValue: value,
        isStructuredType: true,
      };
    } catch {
      return {
        type: CellContentType.PLAIN_TEXT,
        displayPreview: capClassifiedDisplayPreview(String(value)),
        rawValue: value,
        parsedValue: value,
        isStructuredType: false,
      };
    }
  }

  if (typeof value === "string") {
    const isOverScanLimit = value.length > CLASSIFY_SCAN_LIMIT;
    const scanValue = isOverScanLimit ? value.slice(0, CLASSIFY_SCAN_LIMIT) : value;
    const trimmedValue = scanValue.trim();

    if (!isOverScanLimit && isStandalonePublicUrl(trimmedValue)) {
      const linkPayload = buildPublicUrlLinkPayload(trimmedValue);

      return {
        type: CellContentType.LINK,
        displayPreview: capClassifiedDisplayPreview(linkPayload.label),
        rawValue: value,
        parsedValue: linkPayload,
        isStructuredType: true,
        linkPayload,
      };
    }

    if (displayType === "object") {
      return {
        type: CellContentType.JSON,
        displayPreview: capClassifiedDisplayPreview(value),
        rawValue: value,
        parsedValue: undefined,
        isStructuredType: true,
      };
    }

    if (displayType === "markdown" || displayType === "html") {
      return {
        type: CellContentType.MARKDOWN,
        displayPreview: capClassifiedDisplayPreview(value),
        rawValue: value,
        parsedValue: value,
        isStructuredType: true,
      };
    }

    const firstChar = trimmedValue[0];
    const couldBeJsonObjectOrArray = firstChar === "{" || firstChar === "[";

    if (displayType !== "string" && couldBeJsonObjectOrArray) {
      if (isOverScanLimit) {
        return {
          type: CellContentType.JSON,
          displayPreview: capClassifiedDisplayPreview(value),
          rawValue: value,
          parsedValue: undefined,
          isStructuredType: true,
        };
      }

      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "object" && parsed !== null) {
          return {
            type: CellContentType.JSON,
            displayPreview: capClassifiedDisplayPreview(value),
            rawValue: value,
            parsedValue: parsed,
            isStructuredType: true,
          };
        }
      } catch {
        // Plain string; handled below.
      }
    }

    if (parseMarkdownCodeFence(scanValue)) {
      return {
        type: CellContentType.MARKDOWN,
        displayPreview: capClassifiedDisplayPreview(value),
        rawValue: value,
        parsedValue: value,
        isStructuredType: true,
      };
    }
  }

  return {
    type: CellContentType.PLAIN_TEXT,
    displayPreview: capClassifiedDisplayPreview(String(value)),
    rawValue: value,
    parsedValue: value,
    isStructuredType: false,
  };
}
